import {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useState
} from 'react';

import { type InputDateProps } from './interface';
import { useToggle } from '../../hooks/useToggle';

const DATEPICKER_LAYER_ID = '.j-JDSInputDate-calendar-container';

export interface NormalisedDate {
  day?: number;
  month?: number;
  year?: number;
}

export const getNormalisedDate = (value?: string | Date) => {
  if (!value) return { day: undefined, month: undefined, year: undefined };

  const date = new Date(value);

  if (!date.getTime()) {
    return { day: undefined, month: undefined, year: undefined };
  }
  return {
    day: date.getDate(),
    month: date.getMonth() + 1,
    year: date.getFullYear()
  };
};

export const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

export const validateYear = (
  value: NormalisedDate,
  min?: string | Date,
  max?: string | Date
) => {
  const { year = 0 } = value;
  const { year: minYear = 0 } = getNormalisedDate(min);
  const { year: maxYear = 0 } = getNormalisedDate(max);
  if (min && year) {
    if (year < minYear) {
      return false;
    }
  }
  if (max && year) {
    if (year > maxYear) {
      return false;
    }
  }
  return true;
};

export const validateMonth = (
  value: NormalisedDate,
  min?: string | Date,
  max?: string | Date
) => {
  if (!validateYear(value, min, max)) return false;
  const { month = 0 } = value;
  const { month: minMonth = 0 } = getNormalisedDate(min);
  const { month: maxMonth = 0 } = getNormalisedDate(max);
  if (min && month) {
    if (month < minMonth) {
      return false;
    }
  }
  if (max && month) {
    if (month > maxMonth) {
      return false;
    }
  }
  return true;
};

export const validateDate = (
  value: NormalisedDate,
  min?: string | Date,
  max?: string | Date
) => {
  if (!validateYear(value, min, max)) return false;
  if (!validateMonth(value, min, max)) return false;
  if (!value.day || !value.month || !value.year) return true;

  const currentDate = new Date(value.year, value.month - 1, value.day);
  if (min) {
    const minDate = new Date(min);
    if (!minDate.getTime()) return true;
    if (currentDate < minDate) return false;
  }
  if (max) {
    const maxDate = new Date(max);
    if (!maxDate.getTime()) return true;
    if (currentDate > maxDate) return false;
  }
  return true;
};

export const nodeJump = (
  key: string,
  sourceNode?: HTMLInputElement,
  nextNode?: HTMLInputElement,
  prevNode?: HTMLInputElement,
  sourceNodeError?: boolean
) => {
  switch (key) {
    case 'Backspace':
      if (!sourceNode?.value && !sourceNodeError)
        setTimeout(() => prevNode?.select());
      break;
    case 'ArrowLeft':
      if (
        document.getSelection()?.toString() === sourceNode?.value &&
        !sourceNodeError
      ) {
        setTimeout(() => prevNode?.select?.());
      }

      break;
    case 'ArrowRight':
      if (
        document.getSelection()?.toString() === sourceNode?.value &&
        !sourceNodeError
      ) {
        setTimeout(() => nextNode?.select?.());
      }
      break;
    default:
      break;
  }
};

export const getDateFromObject = (obj: NormalisedDate) =>
  new Date(obj.year || 0, obj.month || 0, obj.day);

export const getDateStringFromObject = (obj: NormalisedDate) =>
  `${obj.year || ''}/${(obj.month || '').toString().padStart(2, '0')}/${(
    obj.day || ''
  )
    .toString()
    .padStart(2, '0')}`;

export const getDisabledsDays = (date: NormalisedDate, step?: number) => {
  if (!step) return [];
  const { month = 0, year = 0 } = date;
  const daysInMonth = new Date(year, month, 0).getDate();
  const numbers = Array.from({ length: daysInMonth }).map((_, i) => i + 1);
  const filteredNumbers = numbers.filter((i) => {
    return i % step === 0;
  });

  return filteredNumbers.map((i) => new Date(year, month - 1, i));
};

export const withLogic =
  (Component: React.ComponentType<InputDateProps>): React.FC<InputDateProps> =>
  (props: InputDateProps) => {
    const {
      value,
      datePicker,
      min,
      max,
      error,
      errorText,
      errorFieldsObject,
      setValidity,
      setCustomValidity,
      onChange,
      step,
      ...rest
    } = props;
    const { selected, toggle, close } = useToggle(false);
    const [date, setDate] = useState<NormalisedDate>(getNormalisedDate(value));
    const [element, setElement] = useState<HTMLDivElement | null>(null);

    useEffect(() => {
      if (value === '') {
        setDate({ day: undefined, month: undefined, year: undefined });
      } else {
        setDate(getNormalisedDate(value));
      }
    }, [value]);

    const [focus, setFocus] = useState({
      dd: false,
      mm: false,
      yy: false
    });

    const { day, month, year } = date;

    const rootRef = useCallback((element: HTMLDivElement) => {
      if (element) setElement(element);
    }, []);

    const datePickerLayer = element?.querySelector(DATEPICKER_LAYER_ID);

    const inputs = Array.from(
      (element?.querySelectorAll<HTMLInputElement>(
        'input'
      ) as NodeListOf<HTMLInputElement>) || []
    );
    const ddInput = inputs.find((input) => input.placeholder === 'dd');
    const mmInput = inputs.find((input) => input.placeholder === 'mm');
    const yyyInput = inputs.find((input) => input.placeholder === 'yyyy');

    const { dd, mm, yyyy, errorLocalText } =
      setCustomValidity?.(
        new Date(date.year || 0, date.month || 0, date.day)
      ) || {};

    const userValidated = setValidity?.(getDateFromObject(date));

    const dayError =
      !focus.dd &&
      (userValidated ||
        dd ||
        errorFieldsObject?.dd ||
        error ||
        (date.day && !validateDate(date, min, max)) ||
        (rest.required && !date.day));

    const monthError =
      !focus.mm &&
      (userValidated ||
        mm ||
        errorFieldsObject?.mm ||
        error ||
        (date.month && !validateMonth(date, min, max)) ||
        (rest.required && !date.month));

    const yearError =
      !focus.yy &&
      (userValidated ||
        yyyy ||
        errorFieldsObject?.yyyy ||
        error ||
        (date.year && !validateYear(date, min, max)) ||
        (rest.required && !date.year));

    return (
      <Component
        {...{
          ...rest,
          min,
          max,
          errorText: errorLocalText || errorText,
          _datepickerOpen: selected,
          dateAriaLabel: props.dateAriaLabel || 'Input date',
          monthAriaLabel: props.monthAriaLabel || 'Input month',
          yearAriaLabel: props.yearAriaLabel || 'Input year',
          datepickerAriaLabel: props.datepickerAriaLabel || 'Launch datepicker',
          onMonthKeydown: (ev: KeyboardEvent<HTMLInputElement>) => {
            nodeJump(ev.key, mmInput, yyyInput, ddInput, monthError);
          },
          onDateKeydown: (ev: KeyboardEvent<HTMLInputElement>) => {
            nodeJump(ev.key, ddInput, mmInput, undefined, dayError);
          },
          onYearKeydown: (ev: KeyboardEvent<HTMLInputElement>) => {
            nodeJump(ev.key, yyyInput, undefined, mmInput, yearError);
          },
          showCalendar: datePicker,
          onCalendarClick: toggle,
          onDatepickerClose: close,
          rootRef,
          disabledDates: getDisabledsDays(date, parseInt(step as string)),
          ddValue: focus.dd ? day : day?.toString().padStart(2, '0') || '',
          mmValue: focus.mm ? month : month?.toString().padStart(2, '0') || '',
          yyValue: focus.yy ? year : year?.toString().padStart(4, '0') || '',
          onDatepickerSelect: (value: Date) => {
            const date = getNormalisedDate(value);
            setDate(date);
            onChange?.(getDateStringFromObject(date));
          },
          triggerRef: { current: datePickerLayer },
          ddError: dayError,
          mmError: monthError,
          yyError: yearError,
          onDateChange: (ev: ChangeEvent<HTMLInputElement>) => {
            setFocus({ ...focus, dd: true });
            const currentValue = { ...date, day: parseInt(ev.target.value) };
            setDate(currentValue);

            onChange?.(getDateStringFromObject(currentValue));

            if (ev.target.value.length === 2 && !dayError)
              setTimeout(() => mmInput?.select?.());
          },
          onMonthChange: (ev: ChangeEvent<HTMLInputElement>) => {
            setFocus({ ...focus, mm: true });
            const currentValue = { ...date, month: parseInt(ev.target.value) };
            setDate(currentValue);

            onChange?.(getDateStringFromObject(currentValue));

            if (ev.target.value.length === 2 && !monthError)
              setTimeout(() => yyyInput?.select?.());
          },
          onYearChange: (ev: ChangeEvent<HTMLInputElement>) => {
            setFocus({ ...focus, yy: true });
            const currentValue = { ...date, year: parseInt(ev.target.value) };
            setDate(currentValue);
            onChange?.(getDateStringFromObject(currentValue));
          },
          onDateBlur: () => {
            setFocus({ ...focus, dd: false });
          },
          onMonthBlur: () => {
            setFocus({ ...focus, mm: false });
          },
          onYearBlur: () => {
            setFocus({ ...focus, yy: false });
          }
        }}
      />
    );
  };
export { InputDateProps };
